import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetHomeSections } from "../../hooks/home_sections/useGetHomeSections";
import { useDeleteHomeSection } from "../../hooks/home_sections/useDeleteHomeSection";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import HomeSectionsTableRow from "./Table/HomeSectionsTableRow";
import { HomeSectionsTableColumns } from "./Table/HomeSectionsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const HomeSectionsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("cms.manage"),
            create: has("cms.manage"),
            edit: has("cms.manage"),
            delete: has("cms.manage"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [statusFilter, setStatusFilter] = useState("");

    // CRUD Hooks
    const { deleteSectionMutate, isDeleting } = useDeleteHomeSection();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/home-sections",
        deleteSingle: deleteSectionMutate,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, statusFilter, order]
    );

    const { isLoading, error, homeSections, meta } = useGetHomeSections(params);

    // Filters
    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ]
        }
    ];

    const renderRow = (rowParams) => (
        <HomeSectionsTableRow
            key={rowParams.item.id}
            section={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "أقسام الصفحة الرئيسية" : "Home Sections";
    const subtitle = isArabic ? "إدارة محتوى الصفحة الرئيسية" : "Manage home page content";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة قسم" : "Add Section"}
            onAddClick={() => navigate("/home-sections/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading || isDeleting}
            error={error}
            items={homeSections}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={HomeSectionsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDeleting}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, homeSections)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
            }}
            emptyLabelAr={isArabic ? "لا توجد أقسام" : "No home sections"}
            emptyLabelEn={isArabic ? "لا توجد أقسام" : "No home sections"}
        />
    );
};

export default HomeSectionsTable;
